import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Search, Plus, Tag, Loader2, BookOpen, Package,
  Eye, Star, ArrowRight, Share2, ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import PageSkeleton from '../components/PageSkeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarketplacePage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priceTier, setPriceTier] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [page] = useState(1);
  const [tiers, setTiers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [myTab, setMyTab] = useState('browse');
  const [myListings, setMyListings] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [lectures, setLectures] = useState([]);

  const [form, setForm] = useState({ title: '', description: '', subject: '', price_tier: 'free', lecture_id: '', tags: '' });
  const [creating, setCreating] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15, sort_by: sortBy });
      if (search) params.append('search', search);
      if (priceTier) params.append('price_tier', priceTier);
      const res = await axios.get(`${API}/marketplace/listings?${params}`);
      setListings(res.data.listings || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [page, search, priceTier, sortBy]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    axios.get(`${API}/marketplace/tiers`).then(r => setTiers(r.data.tiers || [])).catch(() => {});
    axios.get(`${API}/lectures`).then(r => setLectures(r.data || [])).catch(() => {});
  }, []);

  const fetchMy = async (tab) => {
    setMyTab(tab);
    if (tab === 'my-listings') {
      const res = await axios.get(`${API}/marketplace/my-listings`);
      setMyListings(res.data.listings || []);
    } else if (tab === 'my-purchases') {
      const res = await axios.get(`${API}/marketplace/my-purchases`);
      setMyPurchases(res.data.purchases || []);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error('Enter a title');
    setCreating(true);
    try {
      await axios.post(`${API}/marketplace/listings`, {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success('Listing created!');
      setShowCreate(false);
      setForm({ title: '', description: '', subject: '', price_tier: 'free', lecture_id: '', tags: '' });
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  const handlePurchase = async (listing) => {
    if (listing.is_own) return toast.error("Can't purchase your own listing");
    if (listing.is_purchased) return toast.info('Already purchased');
    try {
      if (listing.price_amount > 0) {
        const res = await axios.post(`${API}/payments/initialize-marketplace`, {
          listing_id: listing.id,
        });
        const { authorization_url } = res.data;
        if (authorization_url) {
          window.location.href = authorization_url;
        } else {
          toast.error('Could not initialize payment. Please try again.');
        }
        return;
      }
      await axios.post(`${API}/marketplace/purchase`, { listing_id: listing.id });
      toast.success('Purchased! Check your library.');
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Purchase failed. Please try again.'); }
  };

  return (
    <div className="f500-page max-w-7xl mx-auto" data-testid="marketplace-page">
      {/* Hero */}
      <div className="border-b border-[var(--f500-line)] pb-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="f500-eyebrow mb-3">— Marketplace</p>
            <h1 className="f500-display text-4xl sm:text-5xl lg:text-6xl">
              Notes that<br />pay you back.
            </h1>
            <p className="f500-body text-base mt-4 max-w-xl">
              Browse curated study notes from top students. Or list your own and earn while others learn.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <button
              onClick={() => setShowCreate(true)}
              className="f500-btn f500-btn-primary"
              data-testid="create-listing-btn"
            >
              <Plus className="w-4 h-4" /> Sell Notes
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="f500-tabs" role="tablist">
          {[{ id: 'browse', label: 'Browse' }, { id: 'my-listings', label: 'My Listings' }, { id: 'my-purchases', label: 'Purchased' }].map(t => (
            <button
              key={t.id}
              onClick={() => fetchMy(t.id)}
              className={`f500-tab ${myTab === t.id ? 'active' : ''}`}
              aria-selected={myTab === t.id}
              data-testid={`mp-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {myTab === 'browse' && (
        <>
          {/* Search + Filters */}
          <div className="flex gap-2 flex-wrap mb-6 pb-6 border-b border-[var(--f500-line)]">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--f500-ink-3)]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search notes by topic, subject, author..."
                className="f500-input-boxed pl-10"
                data-testid="mp-search"
              />
            </div>
            <select
              value={priceTier}
              onChange={e => setPriceTier(e.target.value)}
              className="f500-input-boxed w-auto"
              data-testid="mp-price-filter"
            >
              <option value="">All Prices</option>
              {tiers.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="f500-input-boxed w-auto"
              data-testid="mp-sort"
            >
              <option value="recent">Newest</option>
              <option value="popular">Most Popular</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>

          {/* Listings Grid */}
          {loading ? (
            <div data-testid="mp-loading"><PageSkeleton variant="grid" rows={6} /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="listings-grid">
              {listings.length === 0 ? (
                <div className="col-span-full f500-card-soft p-12 text-center">
                  <Package className="w-10 h-10 mx-auto mb-3 text-[var(--f500-ink-3)]" />
                  <p className="f500-body text-sm">No listings yet. Be the first to sell your notes.</p>
                </div>
              ) : listings.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="f500-card flex flex-col"
                  data-testid={`listing-${l.id}`}
                >
                  {/* Price band */}
                  <div className="px-5 py-3 border-b border-[var(--f500-line)] flex items-center justify-between">
                    <span className="f500-eyebrow">{l.subject || 'General'}</span>
                    <span className={`f500-mono text-sm font-semibold ${l.price_amount === 0 ? 'text-[var(--f500-green)]' : 'text-[var(--f500-blue)]'}`}>
                      {l.price_label || 'Free'}
                    </span>
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="f500-h3 text-base mb-2 line-clamp-2">{l.title}</h3>
                    <p className="f500-body text-sm line-clamp-3 mb-4 flex-1">{l.description || 'No description provided.'}</p>

                    <div className="flex items-center gap-3 text-xs text-[var(--f500-ink-3)] mb-4 pb-4 border-b border-[var(--f500-line)]" style={{ fontFamily: 'var(--sf-text)' }}>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {l.purchases}</span>
                      {l.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {l.rating}</span>}
                      <span className="ml-auto truncate">{l.seller_name}</span>
                    </div>

                    <div className="flex gap-2">
                      {l.is_purchased ? (
                        <button disabled className="f500-btn f500-btn-secondary flex-1 text-xs">Purchased</button>
                      ) : l.is_own ? (
                        <button disabled className="f500-btn f500-btn-secondary flex-1 text-xs">Your Listing</button>
                      ) : (
                        <button
                          onClick={() => handlePurchase(l)}
                          className="f500-btn f500-btn-primary flex-1 text-xs"
                          data-testid={`buy-${l.id}`}
                        >
                          {l.price_amount === 0 ? 'Get Free' : `Buy ${l.price_label}`}
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/marketplace?listing=${l.id}`;
                          if (navigator.share) {
                            navigator.share({ title: l.title, text: `Check out "${l.title}" on iStudyPro Marketplace`, url }).catch(() => {});
                          } else {
                            navigator.clipboard.writeText(url);
                            toast.success('Link copied to clipboard!');
                          }
                        }}
                        className="f500-btn f500-btn-secondary text-xs px-3"
                        data-testid={`share-mp-${l.id}`}
                        title="Share this listing"
                      >
                        <Share2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {myTab === 'my-listings' && (
        <div className="space-y-2" data-testid="my-listings">
          {myListings.length === 0 ? (
            <p className="f500-body text-sm text-center py-12">No listings yet.</p>
          ) : myListings.map(l => (
            <div key={l.id} className="f500-card p-5 flex items-center gap-4">
              <div className="flex-1">
                <p className="f500-h3 text-sm">{l.title}</p>
                <p className="text-xs text-[var(--f500-ink-3)] mt-1" style={{ fontFamily: 'var(--sf-text)' }}>
                  {l.purchases} sales · {l.price_label}
                </p>
              </div>
              {l.rating > 0 && (
                <span className="f500-badge">
                  <Star className="w-3 h-3" /> {l.rating}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {myTab === 'my-purchases' && (
        <div className="space-y-2" data-testid="my-purchases">
          {myPurchases.length === 0 ? (
            <p className="f500-body text-sm text-center py-12">No purchases yet.</p>
          ) : myPurchases.map(p => (
            <div key={p.id} className="f500-card p-5 flex items-center gap-4" data-testid={`purchase-${p.id}`}>
              <div className="w-10 h-10 bg-[var(--f500-paper-2)] flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-[var(--f500-blue)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="f500-h3 text-sm truncate">{p.title}</p>
                <p className="text-xs text-[var(--f500-ink-3)] mt-0.5" style={{ fontFamily: 'var(--sf-text)' }}>
                  by {p.seller_name} {p.subject ? `· ${p.subject}` : ''} · {p.price_amount > 0 ? `${p.price_currency} ${p.price_amount}` : 'Free'}
                </p>
              </div>
              {p.lecture_id && (
                <Link to={`/lectures?preview=${p.lecture_id}`}>
                  <button className="f500-btn f500-btn-primary text-xs" data-testid={`access-purchase-${p.id}`}>
                    <ExternalLink className="w-3 h-3" /> Access
                  </button>
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-[var(--f500-line)] text-[var(--f500-ink)] max-w-md rounded-none" data-testid="create-listing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 f500-h2 text-xl">
              <Tag className="w-5 h-5 text-[var(--f500-blue)]" /> Sell Study Notes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="rounded-none border-[var(--f500-line)]" data-testid="cl-title" />
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="rounded-none border-[var(--f500-line)] min-h-[80px]" data-testid="cl-desc" />
            <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Subject (e.g. Biology)" className="rounded-none border-[var(--f500-line)]" data-testid="cl-subject" />
            <select value={form.price_tier} onChange={e => setForm(p => ({ ...p, price_tier: e.target.value }))} className="f500-input-boxed" data-testid="cl-price">
              {tiers.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <select value={form.lecture_id} onChange={e => setForm(p => ({ ...p, lecture_id: e.target.value }))} className="f500-input-boxed" data-testid="cl-lecture">
              <option value="">Link a lecture (optional)</option>
              {lectures.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
            <Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="Tags (comma separated)" className="rounded-none border-[var(--f500-line)]" data-testid="cl-tags" />
            <button onClick={handleCreate} disabled={creating} className="f500-btn f500-btn-primary w-full" data-testid="cl-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Listing'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplacePage;
